import { useMemo, useState } from "react";
import type { SceneState } from "../../../shared/api-contract";
import { useViewer } from "../../../state/ViewerProvider";
import { useDomainState, useEditorStore } from "../../../state/editor/useEditor";
import { Icon } from "../../kit";
import { useManifest } from "../library/useManifest";

/** A ground preset = a combination of the existing shadow/reflection setters.
 * Host-side only — no assets, no contract change.
 *
 * `values` is both the thing we write and the thing we compare the live scene
 * against, so the highlighted card can never drift from what the viewer is
 * actually doing (the panel used to seed `active` to "high-contrast" and claim
 * a preset that was never applied). Only the fields a preset names take part in
 * the match — "Mirror Floor" says nothing about shadow softness, so whatever
 * the Scene tab left there must not un-highlight it. */
interface GroundValues {
  groundShadow: boolean;
  groundReflection: boolean;
  brightness?: number;
  softness?: number;
  reflectionOpacity?: number;
  reflectionBlur?: number;
}

interface GroundPreset {
  id: string;
  label: string;
  icon: string;
  values: GroundValues;
}

const PRESETS: GroundPreset[] = [
  {
    id: "soft-studio", label: "Soft Studio", icon: "blur_on",
    values: { groundShadow: true, groundReflection: false, brightness: 0.45, softness: 0.7 },
  },
  {
    id: "mirror", label: "Mirror Floor", icon: "flip",
    values: { groundShadow: true, groundReflection: true, reflectionOpacity: 0.55, reflectionBlur: 0.15 },
  },
  {
    id: "glossy", label: "Glossy", icon: "water_drop",
    values: { groundShadow: true, groundReflection: true, reflectionOpacity: 0.85, reflectionBlur: 0.02 },
  },
  {
    id: "shadow-only", label: "Shadow Only", icon: "wb_shade",
    values: { groundShadow: true, groundReflection: false, brightness: 0.6, softness: 0.4 },
  },
  {
    id: "high-contrast", label: "High Contrast", icon: "contrast",
    values: { groundShadow: true, groundReflection: false, brightness: 0.85, softness: 0.25 },
  },
  {
    id: "none", label: "None", icon: "block",
    values: { groundShadow: false, groundReflection: false },
  },
];

const near = (a: number, b: number) => Math.abs(a - b) < 1e-3;

/** Does the live scene already match every field this preset names? */
function matches(v: GroundValues, s: SceneState | undefined): boolean {
  if (!s) return false;
  if (s.groundShadow !== v.groundShadow) return false;
  if (s.groundReflection !== v.groundReflection) return false;
  if (v.brightness != null && !near(s.groundShadowBake.brightness, v.brightness)) return false;
  if (v.softness != null && !near(s.groundShadowBake.softness, v.softness)) return false;
  if (v.reflectionOpacity != null && !near(s.groundReflectionOpacity, v.reflectionOpacity)) return false;
  if (v.reflectionBlur != null && !near(s.groundReflectionBlur, v.reflectionBlur)) return false;
  return true;
}

const num = (v: unknown): number | undefined => (typeof v === "number" ? v : undefined);

/**
 * A curated `ground` preset stores its GroundValues as the preset's `values`
 * JSON — there is no file. `matches()` compares the two booleans EXACTLY, so a
 * preset that omits one reads as `false`: an author saying "shadow on" has to
 * say so explicitly.
 */
function curatedToPreset(item: Record<string, unknown> & { id: string; label: string }): GroundPreset {
  return {
    id: item.id,
    label: item.label,
    icon: "landscape",
    values: {
      groundShadow: item.groundShadow === true,
      groundReflection: item.groundReflection === true,
      brightness: num(item.brightness),
      softness: num(item.softness),
      reflectionOpacity: num(item.reflectionOpacity),
      reflectionBlur: num(item.reflectionBlur),
    },
  };
}

/** Ground & shadow presets — one-tap combinations applied over the existing
 * baked-shadow + planar-reflection setters (the Scene tab has the fine knobs). */
export function GroundPanel() {
  const { client } = useViewer();
  const r = client.ring;
  const store = useEditorStore();
  const scene = useDomainState("scene");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Curated ground presets from the library, appended after the built-ins.
  // Values-only (no file), so an unreachable API just leaves the built-ins.
  const { items: curated } = useManifest(null, "ground");
  const presets = useMemo(
    () => [...PRESETS, ...curated.map((c) => curatedToPreset(c as Record<string, unknown> & { id: string; label: string }))],
    [curated],
  );

  const apply = async (p: GroundPreset) => {
    if (busy) return; // overlapping preset writes would interleave setter calls
    setBusy(p.id);
    setError(null);
    const v = p.values;
    try {
      // Every setter is an RPC returning a promise; firing them unawaited left
      // a dead viewer's rejections unhandled and cleared the spinner early.
      await r.setGroundShadow(v.groundShadow);
      await r.setGroundReflection(v.groundReflection);
      if (v.brightness != null || v.softness != null) {
        await r.setGroundShadowBake({
          ...(v.brightness != null ? { brightness: v.brightness } : {}),
          ...(v.softness != null ? { softness: v.softness } : {}),
        });
      }
      if (v.reflectionOpacity != null) await r.setGroundReflectionOpacity(v.reflectionOpacity);
      if (v.reflectionBlur != null) await r.setGroundReflectionBlur(v.reflectionBlur);
      // These are the same engine values the Scene tab's Ground rows render from
      // the `scene` slice; without this re-read those rows (and this panel's own
      // highlight, which is derived from the slice) keep showing pre-apply state.
      await store.refresh("scene");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not apply that ground preset.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col gap-3 p-3">
      <span className="text-[12px] font-semibold text-ed-text-2">Ground presets</span>
      <div className="grid grid-cols-2 gap-2">
        {presets.map((p) => {
          const on = matches(p.values, scene);
          const pending = busy === p.id;
          return (
            <button key={p.id} onClick={() => void apply(p)} disabled={!!busy} aria-busy={pending || undefined}
              className={`flex flex-col items-center gap-1.5 rounded-lg border px-2 py-3 text-[12px] transition-colors ${
                busy && !pending ? "cursor-not-allowed opacity-60 " : ""
              }${on ? "border-ed-accent bg-ed-active text-ed-text" : "border-ed-border bg-white text-ed-text hover:border-ed-accent"}`}>
              <Icon
                name={pending ? "progress_activity" : p.icon}
                size={18}
                className={`${pending ? "animate-spin " : ""}${on ? "text-ed-accent" : "text-ed-text-3"}`}
              />
              {p.label}
            </button>
          );
        })}
      </div>
      {error ? <span className="text-[11px] leading-snug text-ed-red">{error}</span> : null}
      <p className="text-[11px] leading-snug text-ed-text-3">Fine controls (darkness, softness, reflection blur) are in Advanced › Ground, on the right.</p>
    </div>
  );
}
